import { Permission } from '@act/shared/authorization';
import {
  MenuItemConfig,
  IconLibrary,
  AvatarMenu,
  SettingsDrawer,
  useDisplayModeContext,
} from '@act/storybook';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { authService } from 'redux/utils/auth/auth-service';
import { getToken } from 'redux/utils/firebase-auth';
import { useAppNavigation } from '../routing/useAppNavigation';
import { useAppLocation } from '../routing/useAppLocation';
import { useState } from 'react';
import VoicemailFormDialog from 'react-components/header/VoicemailFormDialog';
import { useToast } from '../ReusableToast';

const CarePortalAvatarMenu = () => {
  const navigate = useAppNavigation();
  const user = actingUserAppSelectors.useSelectActingUser();
  const canAccessAdminTools = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ACCESS_ADMIN_TOOLS
  );
  const appLocation = useAppLocation();

  const [voicemailFormOpen, setVoicemailFormOpen] = useState<boolean>(false);
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState<boolean>(false);

  const toast = useToast();
  const { displayMode } = useDisplayModeContext();
  const isCompact = displayMode === 'compact';

  const menuItems: MenuItemConfig[] = [
    {
      type: 'item',
      label: 'Update Voicemail Greeting',
      onClick: () => setVoicemailFormOpen(true),
    },
    {
      type: 'item',
      label: 'Switch Guide View',
      onClick: () => navigate.toGuideList(),
    },
    ...(appLocation !== 'admin-dashboard' && canAccessAdminTools
      ? [
          {
            type: 'item' as const,
            label: 'Switch to Admin Portal',
            onClick: () => navigate.toAdminDashboard(),
          },
        ]
      : []),
    ...(appLocation === 'admin-dashboard'
      ? [
          {
            type: 'item' as const,
            label: 'Switch to Care Portal',
            onClick: () => navigate.toDocket(),
          },
        ]
      : []),
    {
      type: 'divider' as const,
    },
    {
      type: 'item' as const,
      label: 'Settings',
      onClick: () => setSettingsDrawerOpen(true),
      icon: <IconLibrary iconName="Gear" size={24} weight="duotone" />,
    },
    {
      type: 'item' as const,
      label: 'Log Out',
      onClick: () => authService.logout(),
      icon: <IconLibrary iconName="SignOut" />,
    },
    ...(canAccessAdminTools
      ? [
          {
            type: 'item' as const,
            label: 'Copy Token',
            onClick: async () => {
              const token = await getToken();
              await navigator.clipboard.writeText(token);
              toast('Token successfully copied to clipboard!', 'success');
            },
            icon: <IconLibrary iconName="ClipboardText" />,
          },
        ]
      : []),
  ];

  return (
    <>
      <AvatarMenu
        user={user}
        menuItems={menuItems}
        isOnlyFirstLetter
        isCompact={isCompact}
      />
      <VoicemailFormDialog
        open={voicemailFormOpen}
        handleClose={() => setVoicemailFormOpen(false)}
      />
      <SettingsDrawer
        open={settingsDrawerOpen}
        onClose={() => setSettingsDrawerOpen(false)}
      />
    </>
  );
};

export default CarePortalAvatarMenu;
